import * as Sentry from '@sentry/capacitor'
import * as SentryVue from '@sentry/vue'
import { createSentryPiniaPlugin } from '@sentry/vue'
import type { App } from 'vue'
import type { Pinia } from 'pinia'

import { isAndroid, isIOS } from '@/lib/platform'
import { edition } from '@/lib/edition'
import { allowlistStores } from '@/lib/telemetry/sentryStoreAllowlist'
import { scrubEvent } from '@/lib/telemetry/sentryScrub'

/**
 * Error reporting for the patient and therapist builds.
 *
 * Until 2026-09 this application had none at all: no SDK in `package.json`, no
 * `Integration::handles()` on the API, no `app.config.errorHandler`, and no
 * crash reporting in either native project. A recording that silently failed on
 * one iOS version, or an exercise runner that threw on a malformed item, was
 * discoverable only when a therapist phoned. On devices in other people's
 * practices that is the worst possible place to be blind.
 *
 * TelemetryDeck (`lib/telemetry.ts`) is not a substitute and never was — it
 * fires a curated set of product events as a flat string map and swallows its
 * own transport failures. It has never seen a stack trace.
 *
 * ## Why `@sentry/capacitor` and not `@sentry/vue` alone
 *
 * The JS SDK sees JS. On an iPad it would miss exactly the failures that are
 * hardest to reproduce: a Swift crash in a Capacitor plugin, the WebView being
 * killed under memory pressure mid-exercise, a watchdog termination. It would
 * also lose every event that happened while the device was offline, which in a
 * practice with poor wifi is a meaningful share of them. The Capacitor SDK
 * wraps the Vue one and adds the native layer plus an on-device envelope
 * cache that flushes on reconnect.
 *
 * ## Privacy
 *
 * Every hardening decision here is ported from the portal, and the reasoning is
 * in `docs/error-reporting.md`. The short version: the store allowlist is
 * deny-by-default, component props are never attached, console breadcrumbs are
 * off, and `scrubEvent` runs over six further channels including the
 * attachment the Pinia plugin puts on the *hint* rather than the event.
 */

/** Read at init so the whole SDK is a no-op when nothing is configured. */
function dsn(): string {
  return (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim() ?? ''
}

function platform(): 'ios' | 'android' | 'web' {
  if (isIOS) return 'ios'
  if (isAndroid) return 'android'
  return 'web'
}

/**
 * Whether reporting is configured. Exported so a caller can tell "nothing has
 * gone wrong" apart from "nothing is being reported".
 */
export function isErrorReportingEnabled(): boolean {
  return dsn() !== ''
}

/**
 * Installs the Pinia plugin. Must run before `app.use(pinia)` mounts anything,
 * and before `initErrorReporting`, because the plugin registers a global event
 * processor that the SDK picks up when it initialises.
 */
export function installErrorReportingStatePlugin(pinia: Pinia): void {
  pinia.use(
    createSentryPiniaPlugin({
      // Deny-by-default. Without this the plugin attaches a dump of every store
      // to every event, which here means the session token, the support code
      // and the patient's assigned exercises.
      stateTransformer: allowlistStores,
      // Suppresses the action-name breadcrumb, and only that. The plugin also
      // writes post-action state into `contexts.state` and that write is NOT
      // gated by this flag — the allowlist is what covers it, with scrubEvent
      // dropping the context outright as a second layer.
      addBreadcrumbs: false,
    }),
  )
}

export function initErrorReporting(app: App): void {
  if (!isErrorReportingEnabled()) {
    // Expected on the web dev build and in tests. Said once, so "the tracker is
    // quiet" and "the tracker is not configured" are distinguishable.
    if (import.meta.env.DEV) {
      console.info('[sentry] disabled: VITE_SENTRY_DSN is not set')
    }

    return
  }

  Sentry.init(
    {
      dsn: dsn(),
      // `capacitor` / `capacitor-staging` / `capacitor-vpp` / `production`,
      // so a store build and the web build are never mixed up in one view.
      environment: import.meta.env.MODE,
      // Shared with the API and with the source-map upload; without it a stack
      // trace stays minified even once the maps are uploaded.
      release: (import.meta.env.VITE_APP_VERSION as string | undefined) || undefined,
      // `YYMMDDHHT`, e.g. 261013111 — the exact value the binary carries as
      // CURRENT_PROJECT_VERSION / versionCode.
      //
      // This is the protocol-correct field and it is NOT where you will read it
      // today: GlitchTip accepts `dist` and then stores nothing (verified — the
      // event comes back with `dist: null` and no corresponding tag). The
      // `build_number` tag set below is what actually survives. Both are sent
      // so the day GlitchTip implements it, or we move to Sentry proper, the
      // right field is already populated.
      //
      // Safe alongside source maps: symbolication here matches on DEBUG IDS,
      // which are independent of release and dist. Verified against
      // glitch.digola.de — a dist-carrying event still resolved to
      // errorReporting.ts:47:11 with its source line. See
      // docs/error-reporting.md.
      dist: (import.meta.env.VITE_APP_BUILD as string | undefined) || undefined,

      // Native crashes, watchdog terminations and offline buffering. The whole
      // reason for the Capacitor SDK rather than the Vue one.
      enableNative: true,
      enableNativeCrashHandling: true,
      enableWatchdogTerminationTracking: true,
      // The "Sentry is not configured natively" alert. Useful locally, never in
      // front of a patient.
      enableNativeNagger: import.meta.env.DEV,

      integrations: [
        // @sentry/vue attaches every erroring component's props by default. On
        // a training screen those props are the exercise, its items and the
        // patient row. Must stay false.
        SentryVue.vueIntegration({ app, attachProps: false }),
        // A stray console.log of a run result would otherwise ride along with
        // the next error.
        SentryVue.breadcrumbsIntegration({ console: false }),
      ],

      // The hint is not optional: the Pinia state attachment lives on it, and
      // the client appends attachments to the envelope after beforeSend
      // returns. See lib/telemetry/sentryScrub.ts.
      beforeSend: (event, hint) => scrubEvent(event, hint),
    },
    SentryVue.init,
  )

  Sentry.setTag('platform', platform())
  // Consumer vs. the institution-purchased VPP bundle. The two have different
  // entitlement paths and fail differently; without this their issues merge.
  Sentry.setTag('edition', edition)
  // The build number, as a tag rather than only as `dist`, because GlitchTip
  // discards `dist` (see the init options above). Same key TelemetryDeck uses,
  // so "which build?" reads identically in both tools. Without this an issue
  // can only be narrowed to a marketing version, and "1.2.0" can be any number
  // of TestFlight respins.
  const build = import.meta.env.VITE_APP_BUILD as string | undefined
  if (build) Sentry.setTag('build_number', build)
}

/**
 * Identify the current session by opaque id only.
 *
 * Never the patient row, and never the support code. The id is a UUID the
 * tracker cannot resolve to a person, and it is what makes "one device" and
 * "everyone" distinguishable during triage. Mirrors the server's
 * `App\Support\Telemetry\SentryUserContext`.
 */
export function identifyForErrorReporting(id: string, kind: 'patient' | 'therapist'): void {
  Sentry.setUser({ id })
  Sentry.setTag('role', kind)
}

/** Called when a session ends; Sentry's scope would otherwise outlive it. */
export function forgetForErrorReporting(): void {
  Sentry.setUser(null)
}
